import React from 'react';
import { Icon } from '@iconify/react';
import { LoyaltyProgram } from '@/types/loyalty';

interface Props {
  formData: LoyaltyProgram;
  arenaFields?: { id: number; name: string }[];
}

export const LoyaltyRuleSummaryCard: React.FC<Props> = ({
  formData,
  arenaFields = [],
}) => {
  const formatRupiah = (val: number) => {
    return 'Rp. ' + Math.round(val || 0).toLocaleString('id-ID');
  };

  const getSummaryItems = (): string[] => {
    switch (formData.active_system) {
      case 'points': {
        const sourcesMap: Record<string, string> = {
          booking: 'booking lapangan',
          rental: 'sewa & layanan',
          product: 'produk',
        };
        const sourcesOrder = ['booking', 'rental', 'product'];
        const activeSources = (formData.points_sources || []).slice().sort(
          (a, b) => sourcesOrder.indexOf(a) - sourcesOrder.indexOf(b)
        );
        const sourcesText =
          activeSources.length > 0
            ? activeSources.map((s) => sourcesMap[s] || s).join(', ')
            : 'belum ada transaksi terpilih';

        const minRedeem = formData.points_min_redeem || 0;
        const rate = formData.points_redemption_rate || 0;
        const minRedeemValue = minRedeem * rate;

        return [
          `Setiap belanja ${formatRupiah(formData.points_spend_per_point)} dapat 1 poin`,
          `1 poin = ${formatRupiah(rate)} saat ditukar`,
          `Bisa ditukar mulai ${minRedeem.toLocaleString('id-ID')} poin (${formatRupiah(minRedeemValue)})`,
          `Berlaku untuk ${sourcesText}`,
          formData.points_has_expiry
            ? `Poin hangus setelah ${formData.points_expiry_months || 12} bulan`
            : 'Poin berlaku selamanya tanpa kedaluwarsa',
        ];
      }

      case 'membership': {
        const items: string[] = [
          formData.membership_registration_fee > 0
            ? `Biaya daftar ${formatRupiah(formData.membership_registration_fee)}`
            : 'Biaya daftar gratis',
          `Aktif ${formData.membership_duration_months || 12} bulan, lalu perpanjang`,
          `Diskon booking ${formData.membership_booking_discount || 0}%`,
          formData.membership_product_discount > 0
            ? `Diskon sewa & produk ${formData.membership_product_discount}%`
            : 'Tanpa diskon sewa & produk',
        ];

        if (formData.membership_priority_booking) {
          items.push(
            `Bisa booking ${formData.membership_priority_days || 7} hari lebih awal`
          );
        }

        return items;
      }

      case 'stamp': {
        const selectedIds = formData.stamp_applicable_field_ids || [];
        const isAllFields =
          arenaFields.length > 0 && selectedIds.length === arenaFields.length;

        let fieldSummary = 'Berlaku di semua lapangan';
        if (selectedIds.length === 0) {
          fieldSummary = 'Belum ada lapangan dipilih';
        } else if (!isAllFields) {
          const matchedNames = arenaFields
            .filter((f) => selectedIds.includes(f.id))
            .map((f) => f.name);
          fieldSummary =
            matchedNames.length > 0
              ? `Berlaku di ${matchedNames.join(', ')}`
              : `Berlaku di ${selectedIds.length} lapangan terpilih`;
        }

        return [
          `Booking ${formData.stamp_target_count || 10} kali, gratis ${formData.stamp_reward_free_hours || 1} jam main`,
          `Booking minimal ${formData.stamp_min_booking_duration || 1} jam yang dihitung`,
          fieldSummary,
          formData.stamp_has_expiry
            ? `Hitungan reset setelah ${formData.stamp_expiry_months || 6} bulan`
            : 'Hitungan tidak pernah reset',
        ];
      }

      default:
        return [];
    }
  };

  const systemMeta: Record<string, { title: string; icon: string }> = {
    points: { title: 'Sistem poin', icon: 'ph:coins' },
    membership: { title: 'Membership', icon: 'ph:identification-card' },
    stamp: { title: 'Booking gratis', icon: 'ph:ticket' },
  };

  const currentMeta = systemMeta[formData.active_system] || systemMeta.points;
  const items = getSummaryItems();

  return (
    <div className="rounded-2xl border border-[#E0E6ED] bg-white p-5 sm:p-6 shadow-xs">
      <span className="block text-[11px] font-semibold uppercase tracking-[0.8px] text-[#8C8C8C] mb-3.5">
        RINGKASAN
      </span>

      {/* Active System Badge & Title */}
      <div className="flex items-center gap-2.5 mb-4">
        <div className="w-8 h-8 rounded-lg bg-[#FFF5EE] text-[#FC480D] flex items-center justify-center shrink-0">
          <Icon icon={currentMeta.icon} className="w-4 h-4" />
        </div>
        <h4 className="text-sm font-semibold text-[#0E1726]">
          {currentMeta.title}
        </h4>
      </div>

      {/* Checklist Items */}
      <div className="space-y-2.5">
        {items.map((text, idx) => (
          <div key={idx} className="flex items-start gap-2.5">
            <Icon
              icon="ph:check-circle"
              className="w-4 h-4 text-[#0BCF46] mt-0.5 shrink-0"
            />
            <p className="text-xs sm:text-[13px] text-[#0E1726] leading-relaxed">
              {text}
            </p>
          </div>
        ))}
      </div>

      {/* Footer Note */}
      <div className="border-t border-[#F0F2F5] mt-5 pt-4">
        <p className="text-[11px] sm:text-xs text-[#8C8C8C] leading-relaxed">
          Program tampil di aplikasi pelanggan setelah disimpan dan diaktifkan. Mengganti sistem tidak menghapus poin atau stamp yang sudah dikumpulkan pelanggan.
        </p>
      </div>
    </div>
  );
};

export default LoyaltyRuleSummaryCard;
